import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart3,
  FileText,
  Download,
  Loader2,
  CalendarIcon,
  Filter,
  TrendingUp,
  DollarSign,
  Phone,
  Users,
  RefreshCw,
  FileSpreadsheet,
  FileDown,
  Clock,
  CheckCircle,
  XCircle,
  Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientReportsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeReportType, setActiveReportType] = useState('lead_summary');
  const [report, setReport] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [status, setStatus] = useState('');
  const [dialerType, setDialerType] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    fetchSavedReports();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSavedReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-portal/reports/saved`, {
        headers: getAuthHeaders()
      });
      setSavedReports(response.data);
    } catch (error) {
      console.error('Failed to fetch saved reports');
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setReport(null);
    
    try {
      const params = new URLSearchParams();
      params.append('report_type', activeReportType);
      
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (leadTypes.length > 0) params.append('lead_types', leadTypes.join(','));
      if (status) params.append('status', status);
      if (dialerType) params.append('dialer_type', dialerType);
      
      const response = await axios.get(`${API_URL}/api/client-portal/reports/generate?${params}`, {
        headers: getAuthHeaders()
      });
      
      setReport(response.data);
      toast.success('Report generated');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format, saveToStorage = false) => {
    setExporting(true);
    
    try {
      const params = new URLSearchParams();
      params.append('report_type', activeReportType);
      params.append('export_format', format);
      params.append('save_to_storage', saveToStorage);
      
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (leadTypes.length > 0) params.append('lead_types', leadTypes.join(','));
      if (status) params.append('status', status);
      if (dialerType) params.append('dialer_type', dialerType);
      
      const response = await axios.get(`${API_URL}/api/client-portal/reports/export?${params}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `report.${format}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      if (saveToStorage) {
        toast.success('Report exported and saved');
        fetchSavedReports();
      } else {
        toast.success('Report exported');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const toggleLeadType = (type) => {
    if (leadTypes.includes(type)) {
      setLeadTypes(leadTypes.filter(t => t !== type));
    } else {
      setLeadTypes([...leadTypes, type]);
    }
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setLeadTypes([]);
    setStatus('');
    setDialerType('');
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'lead_summary': return <Users className="w-4 h-4" />;
      case 'cost_billing': return <DollarSign className="w-4 h-4" />;
      case 'conversion_tracking': return <TrendingUp className="w-4 h-4" />;
      case 'dialer_delivery': return <Phone className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const formatReportType = (type) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6 p-6" data-testid="client-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Reports
          </h1>
          <p className="text-muted-foreground">Generate and export lead reports</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Report Type */}
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select value={activeReportType} onValueChange={setActiveReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead_summary">Lead Summary</SelectItem>
                  <SelectItem value="cost_billing">Cost & Billing</SelectItem>
                  <SelectItem value="conversion_tracking">Conversion Tracking</SelectItem>
                  <SelectItem value="dialer_delivery">Dialer Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Lead Types */}
            {(activeReportType === 'lead_summary' || activeReportType === 'conversion_tracking') && (
              <div className="space-y-2">
                <Label>Lead Types</Label>
                <div className="flex flex-wrap gap-2">
                  {['cold_call', 'live_transfer', 'data_lead'].map(type => (
                    <Badge
                      key={type}
                      variant={leadTypes.includes(type) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleLeadType(type)}
                    >
                      {type.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Status Filter */}
            {activeReportType === 'lead_summary' && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status || "all"} onValueChange={(val) => setStatus(val === "all" ? "" : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="delivery_failed">Delivery Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Dialer Type Filter */}
            {activeReportType === 'dialer_delivery' && (
              <div className="space-y-2">
                <Label>Dialer</Label>
                <Select value={dialerType || "all"} onValueChange={(val) => setDialerType(val === "all" ? "" : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All dialers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dialers</SelectItem>
                    <SelectItem value="convoso">Convoso</SelectItem>
                    <SelectItem value="vichi">VICIDIAL</SelectItem>
                    <SelectItem value="tld">TLD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-4">
              <Button onClick={generateReport} disabled={loading} className="w-full">
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Generate Report
              </Button>
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Export Buttons */}
          {report && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Export Options</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportReport('csv')}
                      disabled={exporting}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportReport('xlsx')}
                      disabled={exporting}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportReport('pdf')}
                      disabled={exporting}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => exportReport('xlsx', true)}
                      disabled={exporting}
                    >
                      <Save className="w-4 h-4 mr-1" />
                      Save & Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}

          {/* Report Display */}
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Generating report...</p>
              </CardContent>
            </Card>
          ) : report ? (
            <>
              {/* Summary Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(report.summary || {}).map(([key, value]) => (
                  <Card key={key}>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground mb-1">
                        {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </div>
                      <div className="text-2xl font-bold">
                        {typeof value === 'number' && key.includes('cost') 
                          ? `$${value.toFixed(2)}`
                          : typeof value === 'number' && key.includes('rate')
                          ? `${value}%`
                          : value}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Charts/Breakdown */}
              {report.by_type && Object.keys(report.by_type).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">By Lead Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {Object.entries(report.by_type).map(([type, data]) => (
                        <div key={type} className="p-4 bg-muted/50 rounded-lg">
                          <div className="font-medium mb-2">{type.replace('_', ' ')}</div>
                          <div className="text-2xl font-bold">{data.count || data.total}</div>
                          {data.cost !== undefined && (
                            <div className="text-sm text-muted-foreground">${data.cost.toFixed(2)}</div>
                          )}
                          {data.conversion_rate !== undefined && (
                            <div className="text-sm text-emerald-600">{data.conversion_rate}% conversion</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* By Dialer (for dialer report) */}
              {report.by_dialer && Object.keys(report.by_dialer).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">By Dialer</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {Object.entries(report.by_dialer).map(([dialer, data]) => (
                        <div key={dialer} className="p-4 bg-muted/50 rounded-lg">
                          <div className="font-medium mb-2 capitalize">{dialer}</div>
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="text-sm text-muted-foreground">Success</div>
                              <div className="text-xl font-bold text-emerald-600">{data.success}</div>
                            </div>
                            <div>
                              <div className="text-sm text-muted-foreground">Failed</div>
                              <div className="text-xl font-bold text-red-600">{data.failed}</div>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">
                            {data.success_rate}% success rate
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Data Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Data ({(report.leads || report.transactions || []).length} records)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {report.leads ? (
                            <>
                              <TableHead>ID</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Cost</TableHead>
                              <TableHead>Date</TableHead>
                            </>
                          ) : (
                            <>
                              <TableHead>ID</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>Date</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(report.leads || report.transactions || []).slice(0, 50).map((row, i) => (
                          <TableRow key={i}>
                            {report.leads ? (
                              <>
                                <TableCell className="font-mono text-xs">{row.id?.slice(-8)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{row.lead_type}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={row.status === 'delivered' ? 'default' : 'secondary'}>
                                    {row.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>${row.cost?.toFixed(2)}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {row.assigned_at?.slice(0, 10)}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell className="font-mono text-xs">{row.id?.slice(-8)}</TableCell>
                                <TableCell>
                                  <Badge variant={row.type === 'credit' ? 'default' : 'secondary'}>
                                    {row.type}
                                  </Badge>
                                </TableCell>
                                <TableCell className={row.type === 'credit' ? 'text-emerald-600' : 'text-red-600'}>
                                  {row.type === 'credit' ? '+' : '-'}${row.amount?.toFixed(2)}
                                </TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {row.created_at?.slice(0, 10)}
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {(report.leads || report.transactions || []).length > 50 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Showing 50 of {(report.leads || report.transactions || []).length} records. Export for full data.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium mb-2">Generate a Report</h3>
                <p className="text-muted-foreground">
                  Select a report type and filters, then click "Generate Report"
                </p>
              </CardContent>
            </Card>
          )}

          {/* Saved Reports */}
          {savedReports.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Saved Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {savedReports.slice(0, 10).map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {getReportTypeIcon(r.report_type)}
                        <div>
                          <div className="font-medium">{formatReportType(r.report_type)}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.created_at?.slice(0, 10)} • {r.filename}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline">{r.file_extension?.toUpperCase()}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
